import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GameOptions from "./GameOptions";
import { useGameBot } from "./useGameBot";
import PlayerNameInput from "./PlayerNameInput";

const MancalaGame = ({ onBack }) => {
  const navigate = useNavigate();
  // Board state: 14 pits.
  // 0-5: Player 1's pits (Bottom, Left to Right)
  // 6: Player 1's store (Right)
  // 7-12: Player 2's pits (Top, Right to Left logic usually, but array is sequential)
  // 13: Player 2's store (Left)
  const [board, setBoard] = useState(Array(14).fill(4).map((_, i) => (i === 6 || i === 13) ? 0 : 4));
  const [gameStarted, setGameStarted] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [message, setMessage] = useState("");
  const [moveCount, setMoveCount] = useState(0); // Trigger for bot re-eval on same-turn moves

  // Settings
  const [gameMode, setGameMode] = useState("PvP");
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  const startGame = () => {
    // Standard setup: 4 seeds in pits 0-5 and 7-12. Stores 6 and 13 empty.
    const newBoard = Array(14).fill(0);
    for (let i = 0; i < 6; i++) {
        newBoard[i] = 4;
        newBoard[7 + i] = 4;
    }
    setBoard(newBoard);
    setGameStarted(true);
    setCurrentPlayer(1);
    setGameOver(false);
    setWinner(null);
    setMessage("Player 1's Turn");
  };

  const getPlayerName = (id) => {
    if (gameMode === "PvC" && id === 2) return "Computer";
    return id === 1 ? player1Name : player2Name;
  };

  const checkGameOver = (currentBoard) => {
      // Game ends when one side is empty
      const p1Empty = currentBoard.slice(0, 6).every(n => n === 0);
      const p2Empty = currentBoard.slice(7, 13).every(n => n === 0);

      if (p1Empty || p2Empty) {
          // Capture remaining
          const finalBoard = [...currentBoard];
          let p1Sum = finalBoard[6];
          let p2Sum = finalBoard[13];

          for(let i = 0; i < 6; i++) {
              p1Sum += finalBoard[i];
              finalBoard[i] = 0;
          }
          for(let i = 7; i < 13; i++) {
              p2Sum += finalBoard[i];
              finalBoard[i] = 0;
          }
          finalBoard[6] = p1Sum;
          finalBoard[13] = p2Sum;
          
          return { isOver: true, finalBoard, winner: p1Sum > p2Sum ? 1 : (p2Sum > p1Sum ? 2 : "Draw") };
      }
      return { isOver: false };
  };

  const handleMove = useCallback((pitIndex, isSimulation = false) => {
     // Game logic needs to be pure if we want to simulate for AI
     // Wrapping in a helper to return new state
     return (currentBoard, currentTurn) => {
         let newBoard = [...currentBoard];
         let seeds = newBoard[pitIndex];
         newBoard[pitIndex] = 0;

         let currentIndex = pitIndex;
         while (seeds > 0) {
             currentIndex = (currentIndex + 1) % 14;
             // Skip opponent's store
             if (currentTurn === 1 && currentIndex === 13) continue;
             if (currentTurn === 2 && currentIndex === 6) continue;

             newBoard[currentIndex]++;
             seeds--;
         }

         let nextTurn = currentTurn === 1 ? 2 : 1;
         let statusMessage = "";

         // Rules:
         // 1. If last seed lands in store, go again
         const myStore = currentTurn === 1 ? 6 : 13;
         if (currentIndex === myStore) {
             nextTurn = currentTurn; // Correct: Keep turn
             statusMessage = "Extra Turn!";
         } 
         // 2. Capture: If last seed lands in empty pit on my side, and opposite has seeds
         else {
             const isMySide = currentTurn === 1 ? (currentIndex >= 0 && currentIndex <= 5) : (currentIndex >= 7 && currentIndex <= 12);
             if (isMySide && newBoard[currentIndex] === 1) {
                 const oppositeIndex = 12 - currentIndex;
                 if (newBoard[oppositeIndex] > 0) {
                     // Capture
                     const captured = newBoard[oppositeIndex] + 1; // +1 is the one just placed
                     newBoard[oppositeIndex] = 0;
                     newBoard[currentIndex] = 0;
                     newBoard[myStore] += captured;
                     statusMessage = "Captured!";
                 }
             }
         }

         return { newBoard, nextTurn, statusMessage };
     };
  }, []);

  const executeMove = (pitIndex) => {
      if (gameOver || !gameStarted) return;
      
      const logic = handleMove(pitIndex);
      const { newBoard, nextTurn, statusMessage } = logic(board, currentPlayer);
      
      const result = checkGameOver(newBoard);
      
      setBoard(result.isOver ? result.finalBoard : newBoard);
      if (result.isOver) {
          setGameOver(true);
          setWinner(result.winner);
          setMessage("Game Over");
      } else {
          setCurrentPlayer(nextTurn);
          setMessage(statusMessage || (nextTurn === 1 ? `${getPlayerName(1)}'s turn` : `${getPlayerName(2)}'s turn`));
      }
      setMoveCount(c => c + 1);
  };

  // Bot Logic
  const getBotMove = useCallback((diff, gameState) => {
      const { board, currentPlayer: botPlayer } = gameState;
      // Valid moves for Bot (Player 2 sits at 7-12)
      // Indexes 7, 8, 9, 10, 11, 12
      const offset = botPlayer === 1 ? 0 : 7;
      const validMoves = [];
      for(let i = 0; i < 6; i++) {
          if (board[offset + i] > 0) validMoves.push(offset + i);
      }
      
      if (validMoves.length === 0) return null; // Should be handled by game over check

      if (diff === "Easy") {
          return validMoves[Math.floor(Math.random() * validMoves.length)];
      }

      // Medium/Hard: Simple Greedy (prioritize Score or Extra Turn)
      let bestMove = validMoves[0];
      let bestScore = -Infinity;

      // Simulation logic needed here... reusing handleMove logic logic is tricky inside callback 
      // simplified simulation for now:
      for (let move of validMoves) {
           let score = 0;
           // If we land in store? index + seeds
           // Very rough heuristic:
           let seeds = board[move];
           let landingPos = (move + seeds) % 14; 
           // Adjust for skipping opponent store roughly
           // Exact calculation is better
           
           // Check for extra turn (landing on 13 for P2)
           // P2 7-12. Store 13.
           // Distance to 13 is (13 - move). If seeds == distance, extra turn.
           if (seeds === (13 - move)) score += 10;
           
           // Check for capture? (Hard to simulate precisely without full copy)
           // Prefer moves closer to store?
           score += move; // Arbitrary tie break

           if (score > bestScore) {
               bestScore = score;
               bestMove = move;
           }
      }

      return bestMove;
  }, []);

  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove: executeMove,
    playerTurn: currentPlayer,
    gameStarted,
    gameOver,
    botStrategy: getBotMove,
    gameState: { board, currentPlayer },
    difficulty,
    trigger: moveCount
  });

  const handlePitClick = (index) => {
      if (gameOver || isBotThinking) return;
      if (gameMode === "PvC" && currentPlayer === 2 && index >= 0 && index <= 5) return; // Player 1 clicked P1 pit during P1 turn? No wait.
      
      // Validation
      // Player 1 can only click 0-5
      // Player 2 can only click 7-12
      const isP1Pit = index >= 0 && index <= 5;
      const isP2Pit = index >= 7 && index <= 12;

      if (currentPlayer === 1 && !isP1Pit) return;
      if (currentPlayer === 2 && !isP2Pit) return;
      
      if (board[index] === 0) return; // Empty pit

      executeMove(index);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/games"))}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          ← Back to Games
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-xl p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Mancala</h1>
        <p className="text-center text-gray-600 mb-8">
            Choose a pit to sow seeds. Strategy counts—land in your store for an extra turn, or capture the opponent's seeds!
        </p>

        {!gameStarted ? (
           <div className="text-center">
             <GameOptions
               gameMode={gameMode}
               setGameMode={setGameMode}
               difficulty={difficulty}
               setDifficulty={setDifficulty}
               onStart={startGame}
             />
             <PlayerNameInput
                 player1Name={player1Name}
                 setPlayer1Name={setPlayer1Name}
                 player2Name={player2Name}
                 setPlayer2Name={setPlayer2Name}
                 gameMode={gameMode}
             />
             
             <div className="mt-8 text-gray-600 max-w-2xl mx-auto">
               <h3 className="font-semibold mb-4 text-lg">Game Rules:</h3>
               <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
                 <p>• <strong>Sowing:</strong> Pick up all seeds from one of your pits and drop them one-by-one into the next pits.</p>
                 <p>• <strong>Extra Turn:</strong> If your last seed lands in your scoring store (the big one on your right), you go again!</p>
                 <p>• <strong>Capture:</strong> If your last seed lands in an empty pit on your side, you capture that seed plus all seeds in the opposite pit.</p>
                 <p>• <strong>Winning:</strong> The game ends when one player's side is empty. The player with the most seeds in their store wins.</p>
               </div>
             </div>
           </div>
        ) : (
           <div>
               <div className="flex justify-between items-center mb-8">
                    <div className="text-xl font-bold text-gray-700">
                        {gameOver ? (
                             <span className="text-purple-600">
                                 {winner === "Draw" ? "It's a Draw!" : `${getPlayerName(winner)} Wins!`}
                             </span>
                        ) : (
                             <span>{message} {isBotThinking && "..."}</span>
                        )}
                    </div>
                    <button onClick={() => setGameStarted(false)} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300">
                        Restart
                    </button>
               </div>

               {/* Board UI */}
               <div className="bg-[#DEB887] p-2 md:p-6 rounded-full shadow-inner max-w-full md:max-w-3xl mx-auto border-4 border-[#8B4513] overflow-x-auto">
                   <div className="flex justify-between items-center h-48 md:h-48 min-w-[320px]">
                       
                        {/* Player 2 Store (Left) */}
                       <div className="w-16 md:w-24 h-32 md:h-40 bg-[#5C4033] rounded-3xl flex flex-col justify-center items-center shadow-inner relative mx-1">
                           <span className="text-white font-bold text-2xl z-10">{board[13]}</span>
                           <span className="absolute bottom-2 text-[#DEB887] text-xs font-semibold md:hidden">P2</span>
                           <span className="absolute bottom-2 text-[#DEB887] text-xs font-semibold hidden md:block">{getPlayerName(2)}</span>
                       </div>

                       {/* Pits Area */}
                       <div className="flex flex-col flex-1 mx-4 space-y-4">
                           {/* Player 2 Row (Top) - Indices 12, 11, 10, 9, 8, 7 */}
                           <div className="flex justify-around">
                               {[12, 11, 10, 9, 8, 7].map(i => (
                                   <button 
                                      key={i}
                                      onClick={() => handlePitClick(i)}
                                      disabled={gameOver || isBotThinking || currentPlayer !== 2}
                                      className={`
                                         w-9 h-9 md:w-16 md:h-16 rounded-full bg-[#8B4513] shadow-inner flex items-center justify-center relative transition-transform
                                         ${currentPlayer === 2 && board[i] > 0 && !gameOver ? "hover:scale-110 ring-2 ring-yellow-400 cursor-pointer" : "cursor-default"}
                                      `}
                                   >
                                       <div className="grid grid-cols-2 gap-1 max-w-[70%]">
                                           {Array.from({length: Math.min(10, board[i])}).map((_, idx) => (
                                               <div key={idx} className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-blue-300"></div>
                                           ))}
                                       </div>
                                       <span className="absolute -top-6 text-gray-700 font-bold text-sm hidden md:block">{board[i]}</span>
                                   </button>
                               ))}
                           </div>

                           {/* Player 1 Row (Bottom) - Indices 0, 1, 2, 3, 4, 5 */}
                            <div className="flex justify-around">
                               {[0, 1, 2, 3, 4, 5].map(i => (
                                   <button 
                                      key={i}
                                      onClick={() => handlePitClick(i)}
                                      disabled={gameOver || isBotThinking || currentPlayer !== 1}
                                      className={`
                                         w-9 h-9 md:w-16 md:h-16 rounded-full bg-[#8B4513] shadow-inner flex items-center justify-center relative transition-transform
                                         ${currentPlayer === 1 && board[i] > 0 && !gameOver ? "hover:scale-110 ring-2 ring-yellow-400 cursor-pointer" : "cursor-default"}
                                      `}
                                   >
                                        <div className="grid grid-cols-2 gap-1 max-w-[70%]">
                                           {Array.from({length: Math.min(10, board[i])}).map((_, idx) => (
                                               <div key={idx} className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-green-300"></div>
                                           ))}
                                       </div>
                                       <span className="absolute -bottom-6 text-gray-700 font-bold text-sm hidden md:block">{board[i]}</span>
                                   </button>
                               ))}
                           </div>
                       </div>
                       
                       {/* Player 1 Store (Right) */}
                        <div className="w-16 md:w-24 h-32 md:h-40 bg-[#5C4033] rounded-3xl flex flex-col justify-center items-center shadow-inner relative mx-1">
                           <span className="text-white font-bold text-2xl z-10">{board[6]}</span>
                           <span className="absolute bottom-2 text-[#DEB887] text-xs font-semibold md:hidden">P1</span>
                           <span className="absolute bottom-2 text-[#DEB887] text-xs font-semibold hidden md:block">{getPlayerName(1)}</span>
                       </div>

                   </div>
               </div>
               
               <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                   <div className="p-4 bg-green-100 rounded-lg">
                       <h3 className="font-bold text-green-800">{getPlayerName(1)}</h3>
                       <p className="text-2xl">{board[6]}</p>
                   </div>
                   <div className="p-4 bg-blue-100 rounded-lg">
                       <h3 className="font-bold text-blue-800">{getPlayerName(2)}</h3>
                       <p className="text-2xl">{board[13]}</p>
                   </div>
               </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default MancalaGame;
